import * as _ from 'lodash';
import { useCallback, useMemo, useState } from 'react';

export type ModalState = {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
};

/**
 * Hook for managing multiple modal states. Useful on pages where we use more than one modal.
 * Pass a list of ids for your modals (as ...args) and you will get back an object of your
 * modal IDs with a UseMultiModal object for managing that modal.
 *
 * @param {...string[]} modalIds - Every argument is a string ID for a modal.
 * @example
 * const ManyModals = () => {
 *   const { fooModal, barModal, bazModal } = useMultipleModals('foo', 'bar', 'baz')
 *   return (
 *     <div>
 *       <Button onClick={fooModal.onOpen}>Open Foo</Button>
 *       <Modal
 *         id="fooModal"
 *         show={fooModal.isOpen}
 *         onHide={fooModal.onClose}
 *       />
 *       <Button onClick={barModal.onOpen}>Open Bar</Button>
 *       <Modal
 *         id="barModal"
 *         show={barModal.isOpen}
 *         onHide={barModal.onClose}
 *       />
 *       <Button onClick={bazModal.onOpen}>Open Baz</Button>
 *       <Modal
 *         id="bazModal"
 *         show={bazModal.isOpen}
 *         onHide={bazModal.onClose}
 *       />
 *     </div>
 *   );
 * };
 */
const useMultipleModals = <T extends string>(...modalIds: T[]): Record<`${T}Modal`, ModalState> => {
  //  modalState is a object where each key is a modal ID and the value is the `isOpen` state of that modal.
  const [modalState, setModals] = useState<Record<string, boolean>>(
    //  Filter out any non-string values.
    //  All modals start out closed, hence the false.
    Object.fromEntries(modalIds.filter(_.isString).map(id => [id, false])),
  );

  //  Callback which returns the open modal handler.
  const onOpenModal = useCallback((modalId: string) => () => setModals(prevModals => ({
    ...prevModals,
    [modalId]: true,
  })), [setModals]);

  //  Callback which returns the open close handler.
  const onCloseModal = useCallback((modalId: string) => () => setModals(prevModals => ({
    ...prevModals,
    [modalId]: false,
  })), [setModals]);

  //  Generates the UseMultiModal object.
  const modalFactory = useCallback((modalId: string, isOpen: boolean): ModalState => ({
    isOpen,
    onOpen: onOpenModal(modalId),
    onClose: onCloseModal(modalId),
  }), [onOpenModal, onCloseModal]);

  //  Creates the UseMultiModal object with the handlers.
  const modals = useMemo(() => Object.entries(modalState)
    .reduce((acc, [modalId, isOpen]) => {
      Object.assign(acc, { [`${modalId}Modal`]: modalFactory(modalId, isOpen) });
      return acc;
    }, {} as Record<`${T}Modal`, ModalState>), [modalState, modalFactory]);

  return modals;
};

export default useMultipleModals;
